import { BookingPaymentChoiceSchema, ClassPolicySchema } from '../../../demo/lib/crm/direct-booking-contract';
import { z } from 'zod';
// Shared allowance validates calendar dates and exact daily-balance arithmetic.
export { ConciergeRecoveryRequest, ConciergeRecoveryResponse, AiAllowanceSchema, ConciergeGuestSession, ConciergeResponse, ConciergeTurnRequest, ConciergeConfirmationRequest, ConciergeConfirmationResult, ConciergePendingAction, ConciergeCard, ConciergePackTarget } from '../../../demo/lib/ai/concierge-contract';
export { ConversationSummarySchema, ConversationPageSchema, MessagePageSchema, CustomerMemorySchema, MemoryPageSchema, AppendMessagesSchema, CreateConversationSchema, CreateMemorySchema, EditMemorySchema } from '../../../demo/lib/conversations/contracts';
export { InboxEventSchema, InboxPageSchema, InboxReadSchema, PushRegistrationSchema, PushRegistrationResultSchema, PushRevocationSchema } from '../../../demo/lib/notifications/contracts';

const PackPolicySchema = z.object({
  cutoffMinutes: z.number().int().min(0).max(43200), lateCancellationConsumesCredit: z.boolean(), noShowConsumesCredit: z.boolean(),
});
const PackTermsShape = {
  eligibleServiceIds: z.array(z.string().min(1)).nullable().optional(), sessionOnly: z.boolean().optional(),
  policySnapshot: PackPolicySchema.nullable().optional(),
};
const PackBalancesSchema = z.object({ available: z.number().int().nonnegative(), held: z.number().int().nonnegative(),
  booked: z.number().int().nonnegative(), used: z.number().int().nonnegative(), expired: z.number().int().nonnegative() });
const PackHistorySchema = z.array(z.object({ eventKey: z.string().min(1),
  event: z.enum(['opening','reserve','book','use','release','cancel','no_show','expire','adjustment']),
  bookingId: z.string().optional(), occurredAt: z.string().datetime({ offset: true }), balances: PackBalancesSchema.nullable() }));
const PackSnapshotSchema = z.object({ ...PackTermsShape, subscriptionId: z.string().min(1), planName: z.string(),
  priceCents: z.number().int().nonnegative(), expiresAt: z.string().datetime({ offset: true }) });


export const ConsumerAccountSchema = z.object({ customerId: z.string().min(1) }).strict();
export const DeleteConsumerAccountRequestSchema = z.object({ confirmation: z.literal('ELIMINAR') }).strict();
export const DeleteConsumerAccountResponseSchema = z.object({
  status: z.literal('deleted'),
  retentionPolicyVersion: z.literal('2026-09-09'),
  appleRevocation: z.enum(['not_applicable', 'pending', 'manual']),
}).strict();
export const SocialChallengeResponseSchema = z.object({
  nonce: z.string().regex(/^[a-f0-9]{64}$/),
  expiresAt: z.string().datetime(),
}).strict();

/**
 * Response contracts for the business dashboard API. These mirror the shapes
 * documented in services/api (Business, Booking, Offer, StaffMember, etc.)
 * closely enough to parse real responses, without re-declaring every field
 * the dashboard never reads.
 */

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

export const RoleSchema = z.enum(['owner', 'employee']);

/**
 * Permisos por perfil (contract section of 2026-09-29). An employee access
 * carries one configurable profile; an owner carries none and holds every key.
 */
export const AccessProfileSchema = z.enum(['admin', 'front_desk', 'coach', 'waiter', 'accountant']);
export type AccessProfile = z.infer<typeof AccessProfileSchema>;

/** The permission catalog, in the API's order (`services/api/src/business-permissions.ts`). */
export const PermissionKeySchema = z.enum([
  'agenda',
  'asistencia',
  'clases',
  'clientes',
  'cobrar',
  'pagos',
  'anular',
  'facturas',
  'finanzas',
  'servicios',
  'horario',
  'negocio',
  'inteligencia',
]);
export type PermissionKey = z.infer<typeof PermissionKeySchema>;
export type Role = z.infer<typeof RoleSchema>;

export const ApiErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    fields: z.record(z.string(), z.string()).optional(),
    /** `version_conflict` only (docs/versiones-contra-choques.md). */
    currentVersion: z.number().int().nonnegative().optional(),
    current: z.unknown().optional(),
  }),
});
export type ApiErrorEnvelope = z.infer<typeof ApiErrorEnvelopeSchema>;

// ---------------------------------------------------------------------------
// Auth (Acceso: email + contraseña; see docs/CONCEPTS.md)
// ---------------------------------------------------------------------------

/** `sessionToken` is present for bearer sessions only; cookie sessions omit it. */
export const LoginResponseSchema = z.object({
  sessionTransport: z.enum(['cookie', 'bearer']),
  sessionToken: z.string().optional(),
  businessUserId: z.string(),
  businessId: z.string(),
  role: RoleSchema,
  /** The roster entry a coach login works as; absent or null for the whole agenda. */
  staffMemberId: z.string().nullable().optional(),
  /** Null for an owner. Optional so an API without profiles still parses. */
  accessProfile: AccessProfileSchema.nullable().optional(),
  /** What this session may do at `businessId`; an owner holds every key. */
  permissions: z.array(PermissionKeySchema).optional(),
  expiresAt: z.string(),
  mustChangePassword: z.boolean(),
  /** A new signup waiting for its card (Stripe) whose free trial is over: the panel opens on the card step. Absent on an API without Stripe. */
  cardRequired: z.boolean().optional(),
  /**
   * When the card lock starts (the free trial's end) while the card is still
   * pending; null when nothing will lock. Absent on an API without Stripe.
   */
  trialEndsAt: z.string().nullable().optional(),
  /**
   * Present only when the login carried a WhatsApp the person had just proven:
   * whether that number now opens this access. A failed link never fails the login.
   */
  whatsappLinked: z.boolean().optional(),
  whatsappLinkError: z.string().optional(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/**
 * `POST /v1/business/auth/whatsapp/start`: the message the person sends Dale
 * from their own WhatsApp, and the poll token that alone collects the session.
 */
export const WhatsAppLoginChallengeSchema = z.object({
  challengeId: z.string().min(1),
  pollToken: z.string().min(1),
  whatsappUrl: z.string().url(),
  text: z.string().min(1),
  expiresInSec: z.number().positive(),
  pollAfterSec: z.number().positive(),
});
export type WhatsAppLoginChallenge = z.infer<typeof WhatsAppLoginChallengeSchema>;

/** `POST /v1/business/auth/whatsapp/complete`: 202 until the message arrives, then the login body. */
export const WhatsAppLoginPollSchema = z.union([
  z.object({ status: z.literal('pending') }),
  LoginResponseSchema,
]);
export type WhatsAppLoginPoll = z.infer<typeof WhatsAppLoginPollSchema>;

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------

export const BookingStatusSchema = z.enum([
  'held',
  'confirmed',
  'completed',
  'paid',
  'released',
  'cancelled',
  'no_show',
]);
export type BookingStatus = z.infer<typeof BookingStatusSchema>;

const AppliedOfferSchema = z.object({
  offerId: z.string(),
  title: z.string(),
  percentOff: z.number(),
});

const PreferenceCueSchema = z.object({
  preferenceId: z.string(),
  message: z.string(),
  sentAt: z.string(),
});

export const BookingSchema = z.object({
  sessionId: z.string().min(1).optional(),
  sessionVersion: z.number().int().positive().optional(),
  subscriptionId: z.string().optional(),
  packSnapshot: PackSnapshotSchema.optional(),
  sessionPolicy: ClassPolicySchema.optional(),

  id: z.string(),
  businessId: z.string(),
  serviceId: z.string(),
  slotId: z.string(),
  startsAt: z.string(),
  durationMin: z.number(),
  partySize: z.number().optional(),
  status: BookingStatusSchema,
  source: z.enum(['user', 'proactive']),
  amountCents: z.number(),
  holdExpiresAt: z.string().optional(),
  offer: AppliedOfferSchema.optional(),
  cue: PreferenceCueSchema.optional(),
  staffId: z.string().optional(),
  tableId: z.string().nullable().optional(),
  seriesId: z.string().optional(),
  kind: z.literal('order').optional(),
  fulfillment: z.enum(['pickup', 'delivery']).optional(),
  createdAt: z.string(),
  confirmedAt: z.string().optional(),
  completedAt: z.string().optional(),
  paidAt: z.string().optional(),
  releasedAt: z.string().optional(),
  cancelledAt: z.string().optional(),
  reminderSentAt: z.string().optional(),
  // Who the visit is for. Null when the booking carries no identity; absent on
  // the mark-complete response, which answers with the booking alone.
  customerName: z.string().nullable().optional(),
  customerPhone: z.string().nullable().optional(),
  // Links the visit to the team's ficha on this client (level, tags, notes).
  customerId: z.string().nullable().optional(),
  // What the customer left after the visit (POST /v1/bookings/:id/rating); absent until then.
  rating: z.object({ stars: z.number().int().min(1).max(5), comment: z.string().optional(), createdAt: z.string() }).optional(),
});
export type Booking = z.infer<typeof BookingSchema>;

/** GET /v1/business/ratings: the business sees who rated which visit, newest first. */
export const BusinessRatingsResponseSchema = z.object({
  data: z.object({
    rating: z.number().min(0).max(5),
    count: z.number().int().nonnegative(),
    ratings: z.array(
      z.object({
        id: z.string(),
        bookingId: z.string(),
        stars: z.number().int().min(1).max(5),
        comment: z.string().optional(),
        customerName: z.string(),
        serviceName: z.string().optional(),
        startsAt: z.string(),
        createdAt: z.string(),
      }),
    ),
  }),
});
export type BusinessRatings = z.infer<typeof BusinessRatingsResponseSchema>['data'];

/**
 * GET /v1/business/booking-fees (0092): what an Independientes owner owes Dale
 * for bookings from the app. `invoice` is the one to pay now, with Stripe's
 * own page; `overdue` means the business is out of the app until it is paid.
 */
export const BookingFeesSchema = z.object({
  billed: z.boolean(),
  freePerMonth: z.number().int().nonnegative(),
  unitCents: z.number().int().nonnegative(),
  minimumCents: z.number().int().nonnegative(),
  paymentDays: z.number().int().positive(),
  thisMonth: z.object({
    bookings: z.number().int().nonnegative(),
    billable: z.number().int().nonnegative(),
    amountCents: z.number().int().nonnegative(),
  }),
  pendingCents: z.number().int().nonnegative(),
  invoice: z
    .object({
      id: z.string(),
      amountCents: z.number().int().positive(),
      issuedAt: z.string(),
      dueAt: z.string(),
      payUrl: z.string().url().refine((url) => url.startsWith('https://invoice.stripe.com/'), 'Stripe pay page'),
      overdue: z.boolean(),
    })
    .nullable(),
});
export type BookingFees = z.infer<typeof BookingFeesSchema>;

export const BookingListResponseSchema = z.object({ data: z.array(BookingSchema) });

/** One transition of a booking, oldest first in the detail response. */
export const BookingEventSchema = z.object({
  fromStatus: BookingStatusSchema.nullable(),
  toStatus: BookingStatusSchema,
  actorType: z.enum(['customer', 'business_user', 'system']),
  reason: z.string().nullable(),
  createdAt: z.string(),
});
export type BookingEvent = z.infer<typeof BookingEventSchema>;

export const BookingDetailSchema = BookingSchema.extend({
  events: z.array(BookingEventSchema),
});
export type BookingDetail = z.infer<typeof BookingDetailSchema>;

export const BookingDetailResponseSchema = z.object({ data: BookingDetailSchema });

// ---------------------------------------------------------------------------
// Cues
// ---------------------------------------------------------------------------

export const MediatedCueSchema = z.object({
  cueId: z.string(),
  bookingId: z.string(),
  preferenceId: z.string(),
  message: z.string(),
  sentAt: z.string(),
});
export type MediatedCue = z.infer<typeof MediatedCueSchema>;

export const CueResponseSchema = z.object({ cue: MediatedCueSchema.nullable() });
export type CueResponse = z.infer<typeof CueResponseSchema>;

export const CueAcknowledgeResponseSchema = z.object({ acknowledgedAt: z.string() });

// ---------------------------------------------------------------------------
// Schedule
// ---------------------------------------------------------------------------

export const ScheduleDaySchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  opensAt: z.string().optional(),
  closesAt: z.string().optional(),
  isClosed: z.boolean().optional(),
});
export type ScheduleDay = z.infer<typeof ScheduleDaySchema>;

// ---------------------------------------------------------------------------
// Offers
// ---------------------------------------------------------------------------

/**
 * Exactly one discount shape per Oferta (services/api's `BusinessOfferDiscount`):
 * a percentage of the cart, or a flat number of cents off it.
 */
export const BusinessOfferDiscountSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('percent'), percent: z.number() }),
  z.object({ type: z.literal('fixed'), amountCents: z.number() }),
]);
export type BusinessOfferDiscount = z.infer<typeof BusinessOfferDiscountSchema>;

export const BusinessOfferSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  title: z.string(),
  description: z.string(),
  discount: BusinessOfferDiscountSchema,
  /** Back-compat mirror of `discount.percent`; absent (never 0) for a fixed Oferta. */
  percentOff: z.number().optional(),
  startsAt: z.string(),
  validUntil: z.string(),
  validDays: z.array(z.number()).optional(),
  isActive: z.boolean(),
  /** Versiones contra choques (0099): sent back as expectedVersion on an edit. */
  version: z.number().int().positive().optional(),
});
export type BusinessOffer = z.infer<typeof BusinessOfferSchema>;

export const BusinessOfferListResponseSchema = z.object({ data: z.array(BusinessOfferSchema) });

// ---------------------------------------------------------------------------
// Business (profile, schedule, services, staff, photos)
// ---------------------------------------------------------------------------

export const ServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  durationMin: z.number(),
  priceCents: z.number(),
});
export type Service = z.infer<typeof ServiceSchema>;

export const StaffMemberPublicSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  serviceIds: z.array(z.string()).optional(),
});

const DayWindowSchema = z.tuple([z.number(), z.number()]);

const WeekHoursSchema = z.object({
  mon: DayWindowSchema.nullable().optional(),
  tue: DayWindowSchema.nullable().optional(),
  wed: DayWindowSchema.nullable().optional(),
  thu: DayWindowSchema.nullable().optional(),
  fri: DayWindowSchema.nullable().optional(),
  sat: DayWindowSchema.nullable().optional(),
  sun: DayWindowSchema.nullable().optional(),
  default: DayWindowSchema.optional(),
});
export type WeekHours = z.infer<typeof WeekHoursSchema>;

export const BusinessHoursSchema = z.union([DayWindowSchema, WeekHoursSchema]);
export type BusinessHours = z.infer<typeof BusinessHoursSchema>;

const BusinessPhotoVariantSchema = z.object({
  url: z.string(),
  width: z.number(),
  height: z.number(),
});

export const BusinessPhotoSchema = z.object({
  id: z.string(),
  variants: z.array(BusinessPhotoVariantSchema),
  blurhash: z.string().optional(),
});
export const BusinessPhotosResponseSchema = z.object({ data: z.array(BusinessPhotoSchema) });

export type BusinessPhoto = z.infer<typeof BusinessPhotoSchema>;

const CATEGORY_VALUES = [
  'restaurant',
  'barbershop',
  'salon',
  'doctor',
  'florist',
  'autoshop',
  'gym',
  'nailstudio',
  'spa',
  'classstudio',
  'dogwalker',
  'cleaner',
  'plumber',
  'electrician',
  'handyman',
  'tutor',
  'trainingcenter',
] as const;

/** Panama City: the consumer network. Mirrors ALL_NEIGHBORHOODS in apps/demo/lib/neighborhoods.ts. */
export const PANAMA_NEIGHBORHOOD_VALUES = [
  'Casco Viejo',
  'San Francisco',
  'Obarrio',
  'Costa del Este',
  'El Cangrejo',
  'Bella Vista',
  'Punta Pacífica',
  'Marbella',
  'Río Abajo',
  'Punta Paitilla',
  'Clayton',
  'Amador',
  'Albrook',
  'Nuevo Tocumen',
] as const;

/** Caracas, for Dale Venezuela. Mirrors CARACAS_NEIGHBORHOODS in apps/demo/lib/neighborhoods.ts. */
export const CARACAS_NEIGHBORHOOD_VALUES = [
  'Altamira',
  'Chacao',
  'Las Mercedes',
  'Los Palos Grandes',
  'La Castellana',
  'El Rosal',
  'Los Dos Caminos',
  'Sabana Grande',
  'El Hatillo',
  'La Candelaria',
  'Bello Monte',
  'Santa Fe',
] as const;

const NEIGHBORHOOD_VALUES = [...PANAMA_NEIGHBORHOOD_VALUES, ...CARACAS_NEIGHBORHOOD_VALUES] as const;

export const CategorySchema = z.enum(CATEGORY_VALUES);
export type Category = z.infer<typeof CategorySchema>;
export const NeighborhoodSchema = z.enum(NEIGHBORHOOD_VALUES);
export type Neighborhood = z.infer<typeof NeighborhoodSchema>;

/**
 * A business's place as the API sends it: a Dale zone in Panamá and Venezuela,
 * or since 2 Nov 2026 its own city or area anywhere else ("Brickell"). Typed as
 * a zone for the screens; `countryCode` says which it is
 * (`isZonedCountry`, lib/country.ts).
 */
export const BusinessPlaceSchema = z.union([
  NeighborhoodSchema,
  z
    .string()
    .trim()
    .min(1)
    .max(80)
    .transform((area) => area as Neighborhood),
]);

/** Ascending tiers. Chosen explicitly since migration 0064; the price is the agreed amount. */
export const PlanSchema = z.enum(['reservas', 'base', 'pro', 'max', 'ultra']);

/** Month by month, or the whole year up front (migration 0067). */
export const BillingCycleSchema = z.enum(['monthly', 'annual']);

/** `GET|PUT /v1/business/membership`: the owner's own plan and what each plan would cost them. */
export const MembershipSchema = z.object({
  plan: PlanSchema,
  monthlyPriceCents: z.number().int(),
  listPriceCents: z.number().int(),
  discountPct: z.number(),
  perBookingCents: z.number().int(),
  /** Absent on an API from before annual billing, which only billed monthly. */
  billingCycle: BillingCycleSchema.default('monthly'),
  annualDiscountPct: z.number().optional(),
  planChangedAt: z.string().nullable().optional(),
  /** Versiones contra choques (0099): the version of the plan, price, cycle and trial together; sent back as expectedVersion by the plan change. */
  planVersion: z.number().int().positive().optional(),
  plans: z.array(
    z.object({
      id: PlanSchema,
      listPriceCents: z.number().int(),
      priceCents: z.number().int(),
      /** Twelve months of `priceCents` paid up front; null for Independientes. */
      annualPriceCents: z.number().int().nullable().optional(),
    }),
  ),
});
export type Membership = z.infer<typeof MembershipSchema>;
export const MembershipResponseSchema = z.object({ data: MembershipSchema });

/** The `/v1/business/profile` and `/v1/businesses/:businessId/schedule` response: a Business without photos. */
export const BusinessProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: CategorySchema,
  neighborhood: BusinessPlaceSchema,
  /** ISO code, stored since 2 Nov 2026 (API migration 0096); absent on older businesses. */
  countryCode: z.string().optional(),
  emoji: z.string(),
  tagline: z.string(),
  rating: z.number(),
  priceLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  services: z.array(ServiceSchema),
  staff: z.array(StaffMemberPublicSchema),
  classTimes: z.array(z.string()).optional(),
  hours: BusinessHoursSchema.optional(),
  cuisine: z.string().optional(),
  ambience: z.array(z.string()).optional(),
  /** Display/navigation only: never read by matching or the zone-hop ranking. */
  address: z.string().optional(),
  /** Public venue contact number, distinct from a staff or owner identity phone. */
  contactPhoneE164: z.string().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  /** "Cerrado temporalmente", the owner's own switch; absent means open. */
  isPaused: z.boolean().optional(),
  /** Suspended by the platform; the owner sees it but cannot clear it. */
  isSuspended: z.boolean().optional(),
  /** Subscription tier gating Pro-only screens server-side; absent means 'base'. */
  plan: PlanSchema.optional(),
  monthlyPriceCents: z.number().optional(),
  /** Versiones contra choques (0099), only on the owner's own read: the profile's version. */
  version: z.number().int().positive().optional(),
  /** The weekly hours' own version, sent back by the Horario screen. */
  scheduleVersion: z.number().int().positive().optional(),
});
export type BusinessProfile = z.infer<typeof BusinessProfileSchema>;

/** The `/v1/businesses/:businessId` response: full detail including photos. */
export const BusinessDetailSchema = BusinessProfileSchema.extend({
  photos: z.array(BusinessPhotoSchema).optional(),
});
export type BusinessDetail = z.infer<typeof BusinessDetailSchema>;

// ---------------------------------------------------------------------------
// Operational WhatsApp destination (owner-only)
// ---------------------------------------------------------------------------

/**
 * The destination is only active after the owner confirms the one-time code.
 * The API never returns the full phone number, including after verification.
 */
export const NotificationDestinationSchema = z.object({
  businessId: z.string(),
  channel: z.literal('whatsapp'),
  status: z.enum(['enabled', 'disabled']),
  version: z.number().int().positive(),
  maskedDestination: z.string(),
  verifiedAt: z.string(),
  statusChangedAt: z.string(),
  updatedAt: z.string(),
});
export type NotificationDestination = z.infer<typeof NotificationDestinationSchema>;

export const NotificationDestinationResponseSchema = z.object({
  data: NotificationDestinationSchema.nullable(),
});
export type NotificationDestinationResponse = z.infer<typeof NotificationDestinationResponseSchema>;

export const NotificationVerificationStartResponseSchema = z.object({
  data: z.object({
    challengeId: z.string(),
    maskedDestination: z.string(),
    expiresAt: z.string(),
    resendAfter: z.string(),
  }),
});
export type NotificationVerificationStartResponse = z.infer<
  typeof NotificationVerificationStartResponseSchema
>;

// ---------------------------------------------------------------------------
// Servicios (owner-managed catalog; docs/CONCEPTS.md — "Servicio" is what the
// business charges for, a different object from a Miembro del equipo)
// ---------------------------------------------------------------------------

export const BusinessServiceRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  priceCents: z.number(),
  durationMin: z.number(),
  isTransactional: z.boolean(),
  isActive: z.boolean(),
  /** Versiones contra choques (0099): sent back as expectedVersion on an edit. */
  version: z.number().int().positive().optional(),
});
export type BusinessServiceRecord = z.infer<typeof BusinessServiceRecordSchema>;

export const BusinessServiceListResponseSchema = z.object({
  data: z.array(BusinessServiceRecordSchema),
});

// ---------------------------------------------------------------------------
// Restaurant menu (the item-priced catalog, deliberately separate from
// appointment Services).
// ---------------------------------------------------------------------------

export const MenuProductSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  name: z.string(),
  category: z.string(),
  priceCents: z.number(),
  isActive: z.boolean(),
  sortOrder: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Versiones contra choques (0099): sent back as expectedVersion on an edit. */
  version: z.number().int().positive().optional(),
});
export type MenuProduct = z.infer<typeof MenuProductSchema>;

export const MenuProductListResponseSchema = z.object({ data: z.array(MenuProductSchema) });

export const MenuPhotoUploadSchema = z.object({
  objectKey: z.string(),
  url: z.string(),
  width: z.number(),
  height: z.number(),
});
export type MenuPhotoUpload = z.infer<typeof MenuPhotoUploadSchema>;

export const MenuProductProposalSchema = z.object({
  name: z.string(),
  category: z.string(),
  priceCents: z.number(),
});
export type MenuProductProposal = z.infer<typeof MenuProductProposalSchema>;

export const MenuProductAnalysisResponseSchema = z.object({
  data: z.object({ products: z.array(MenuProductProposalSchema) }),
});

// ---------------------------------------------------------------------------
// La comanda (`tabs` + `tab_items`, migration 0030): the running tab a mesa
// carries until it is settled, by guest or in equal shares.
// ---------------------------------------------------------------------------

export const TabItemSchema = z.object({
  id: z.string(),
  tabId: z.string(),
  /** Null for a typed-in line that never came from the menu. */
  productId: z.string().nullable(),
  name: z.string(),
  unitPriceCents: z.number(),
  quantity: z.number(),
  /** Which puesto ordered it, 1-based. Null is the shared line. */
  guestSlot: z.number().nullable(),
  /** The Cobro that paid this line, or null while it is unpaid. */
  settledChargeId: z.string().nullable(),
  lineTotalCents: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Versiones contra choques (0099): sent back as expectedVersion when the line's quantity or puesto changes. */
  version: z.number().int().positive().optional(),
});
export type TabItem = z.infer<typeof TabItemSchema>;

export const TabSplitPaymentSchema = z.object({
  part: z.number(),
  parts: z.number(),
  chargeId: z.string(),
  amountCents: z.number(),
});
export type TabSplitPayment = z.infer<typeof TabSplitPaymentSchema>;

export const TabRecordSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  tableId: z.string(),
  bookingId: z.string().nullable(),
  status: z.enum(['open', 'settled']),
  items: z.array(TabItemSchema),
  splitPayments: z.array(TabSplitPaymentSchema),
  totalCents: z.number(),
  unsettledCents: z.number(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TabRecord = z.infer<typeof TabRecordSchema>;

export const TabListResponseSchema = z.object({ data: z.array(TabRecordSchema) });
export const TabResponseSchema = z.object({ data: TabRecordSchema });

export const TabSettlementSchema = z.object({
  tab: TabRecordSchema,
  chargeId: z.string(),
  amountCents: z.number(),
});
export type TabSettlement = z.infer<typeof TabSettlementSchema>;

export const TabSettlementResponseSchema = z.object({ data: TabSettlementSchema });

// ---------------------------------------------------------------------------
// Staff (owner CRM view)
// ---------------------------------------------------------------------------

export const BusinessStaffRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  serviceIds: z.array(z.string()).optional(),
  isActive: z.boolean(),
  hours: z.array(ScheduleDaySchema),
  /** Present only while on vacation: the YYYY-MM-DD they come back. */
  awayUntil: z.string().optional(),
  /** Versiones contra choques (0099): the roster row's version, sent back as expectedVersion on an edit. */
  version: z.number().int().positive().optional(),
});
export type BusinessStaffRecord = z.infer<typeof BusinessStaffRecordSchema>;

export const BusinessStaffListResponseSchema = z.object({
  data: z.array(BusinessStaffRecordSchema),
});

// ---------------------------------------------------------------------------
// Accesos (business_users login accounts; owner-only management, see
// docs/CONCEPTS.md — deliberately not the same object as a Miembro/staff
// record above).
// ---------------------------------------------------------------------------

export const BusinessAccessRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Null on a login provisioned with a username only; the API has always allowed it. */
  email: z.string().nullable(),
  /** Nullable/optional to tolerate legacy rows provisioned before usernames existed. */
  username: z.string().optional().nullable(),
  role: RoleSchema,
  isActive: z.boolean(),
  mustChangePassword: z.boolean(),
  /** The roster entry this login works as (a coach); null for the whole agenda. */
  staffMemberId: z.string().nullable().optional(),
  /** Null on an owner row. Linking a roster entry never changes it by itself. */
  accessProfile: AccessProfileSchema.nullable().optional(),
  /** Versiones contra choques (0099): sent back as expectedVersion with a profile or agenda-link edit. */
  version: z.number().int().positive().optional(),
});
export type BusinessAccessRecord = z.infer<typeof BusinessAccessRecordSchema>;

export const BusinessAccessListResponseSchema = z.object({
  data: z.array(BusinessAccessRecordSchema),
});

/** `GET /v1/business/permissions` and `PUT /v1/business/permissions/:profile` (owner only, Pro). */
export const BusinessPermissionsResponseSchema = z.object({
  data: z.object({
    /** Effective lists: what the owner stored at this location, or the defaults. */
    profiles: z.object({
      admin: z.array(PermissionKeySchema),
      front_desk: z.array(PermissionKeySchema),
      coach: z.array(PermissionKeySchema),
      waiter: z.array(PermissionKeySchema),
      accountant: z.array(PermissionKeySchema),
    }),
    keys: z.array(PermissionKeySchema),
    /** Versiones contra choques (0099): one version per profile, 0 when the profile has no stored row. */
    versions: z
      .object({
        admin: z.number().int().nonnegative(),
        front_desk: z.number().int().nonnegative(),
        coach: z.number().int().nonnegative(),
        waiter: z.number().int().nonnegative(),
        accountant: z.number().int().nonnegative(),
      })
      .optional(),
  }),
});
export type BusinessPermissionsResponse = z.infer<typeof BusinessPermissionsResponseSchema>;

/** Body of `PUT /v1/business/permissions/:profile`; the API dedupes and orders it. */
export const BusinessPermissionsUpdateSchema = z
  .object({ permissions: z.array(PermissionKeySchema) })
  .strict();
export type BusinessPermissionsUpdate = z.infer<typeof BusinessPermissionsUpdateSchema>;

/** `GET /v1/business/me`: any business session, what it is and may do here. */
/**
 * Funciones por negocio (contract §Funciones): the CRM features a Dale
 * operator can turn on or off over the plan. Same list as the API's
 * `FEATURE_KEYS` (services/api/src/business-features.ts).
 */
export const FEATURE_KEYS = [
  'inteligencia',
  'agentes',
  'recepcionista',
  'clases',
  'mesas',
  'comandas',
  'facturas',
  'finanzas',
  'ofertas',
  'planes',
  'historial',
] as const;
export const FeatureKeySchema = z.enum(FEATURE_KEYS);
export type FeatureKey = z.infer<typeof FeatureKeySchema>;
export type FeatureMap = Partial<Record<FeatureKey, boolean>>;
const KNOWN_FEATURES: ReadonlySet<string> = new Set(FEATURE_KEYS);
/**
 * Every feature with what this business gets: the plan with any override on
 * top. A key this panel does not know yet is dropped, never refused.
 */
export const FeatureMapSchema = z
  .record(z.string(), z.boolean())
  .transform((map): FeatureMap => Object.fromEntries(Object.entries(map).filter(([key]) => KNOWN_FEATURES.has(key))));

/** Herramientas: which of the four tools every type gets show for this business (`/v1/business/me`). */
export const ToolMapSchema = z.object({
  calendario: z.boolean(),
  planes: z.boolean(),
  espacios: z.boolean(),
  clases: z.boolean(),
});
export type ToolMap = z.infer<typeof ToolMapSchema>;

/** `PUT /v1/business/tools/:tool`: every tool's state after the change. */
export const ToolsResponseSchema = z.object({ data: z.object({ tools: ToolMapSchema }) });

export const BusinessMeResponseSchema = z.object({
  data: z.object({
    businessUserId: z.string(),
    businessId: z.string(),
    role: RoleSchema,
    accessProfile: AccessProfileSchema.nullable(),
    staffMemberId: z.string().nullable(),
    permissions: z.array(PermissionKeySchema),
    /** Absent from an older server: the plan alone decides then. */
    features: FeatureMapSchema.optional(),
    /** Absent from an older server: the business type's defaults decide then. */
    tools: ToolMapSchema.optional(),
  }),
});
export type BusinessMeResponse = z.infer<typeof BusinessMeResponseSchema>;

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export const BusinessCustomerSummarySchema = z.object({
  customerId: z.string(),
  displayName: z.string(),
  maskedPhone: z.string().optional(),
  completedVisits: z.number(),
  upcomingVisits: z.number(),
  /** Confirmed visits the business checked in as a no-show; not a Cobro count. */
  noShowCount: z.number(),
  totalVisits: z.number(),
  firstVisitAt: z.string(),
  lastVisitAt: z.string(),
});
export type BusinessCustomerSummary = z.infer<typeof BusinessCustomerSummarySchema>;

export const BusinessCustomerPageSchema = z.object({
  data: z.array(BusinessCustomerSummarySchema),
  nextCursor: z.string().optional(),
});
export type BusinessCustomerPage = z.infer<typeof BusinessCustomerPageSchema>;

// ---------------------------------------------------------------------------
// Customer file (the team's own ficha on a client: level, tags, notes)
// ---------------------------------------------------------------------------

export const CustomerLevelSchema = z.enum(['principiante', 'intermedio', 'avanzado']);
export type CustomerLevel = z.infer<typeof CustomerLevelSchema>;

/** A travel pause: Panama calendar days, `YYYY-MM-DD`, both inclusive. */
export const CustomerPauseSchema = z.object({
  from: z.string(),
  until: z.string(),
  reason: z.string().nullable(),
});
export type CustomerPause = z.infer<typeof CustomerPauseSchema>;

export const CustomerProfileSchema = z.object({
  customerId: z.string(),
  level: CustomerLevelSchema.nullable(),
  tags: z.array(z.string()),
  // The ficha at a glance (contract 2026-09-29). Defaulted so an API that
  // predates them still parses, as an empty field.
  injuries: z.string().nullable().default(null),
  music: z.string().nullable().default(null),
  favoriteStaffId: z.string().nullable().default(null),
  pause: CustomerPauseSchema.nullable().default(null),
  noteCount: z.number(),
  lastNoteAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  /** Versiones contra choques (0099): a client with no ficha reads 0; notes never move it. Sent back as expectedVersion. */
  version: z.number().int().nonnegative().optional(),
});
export type CustomerProfile = z.infer<typeof CustomerProfileSchema>;

export const CustomerProfileListSchema = z.object({ data: z.array(CustomerProfileSchema) });
export const CustomerProfileResponseSchema = z.object({ data: CustomerProfileSchema });

export const CustomerNoteSchema = z.object({
  id: z.string(),
  body: z.string(),
  authorName: z.string(),
  authorUserId: z.string().nullable(),
  createdAt: z.string(),
  canDelete: z.boolean(),
  // Only ever the authenticated same-origin photo route: the session rides on this fetch.
  photo: z
    .object({
      url: z.string().regex(/^\/v1\/business\/customers\/[A-Za-z0-9_-]+\/notes\/[A-Za-z0-9_-]+\/photo$/),
      width: z.number(),
      height: z.number(),
    })
    .optional(),
});
export type CustomerNote = z.infer<typeof CustomerNoteSchema>;

export const CustomerNoteResponseSchema = z.object({ data: CustomerNoteSchema });

export const CustomerFileSchema = z.object({
  profile: CustomerProfileSchema,
  notes: z.array(CustomerNoteSchema),
});
export type CustomerFile = z.infer<typeof CustomerFileSchema>;

export const CustomerFileResponseSchema = z.object({ data: CustomerFileSchema });

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export const BookingStatusCountsSchema = z.object({
  held: z.number(),
  confirmed: z.number(),
  completed: z.number(),
  paid: z.number(),
  released: z.number(),
  cancelled: z.number(),
});
export type BookingStatusCounts = z.infer<typeof BookingStatusCountsSchema>;

export const BusinessMetricsSchema = z.object({
  from: z.string(),
  to: z.string(),
  bookings: BookingStatusCountsSchema,
  completions: z.number(),
  cues: z.object({ shown: z.number(), acknowledged: z.number() }),
  revenueCents: z.number(),
});
export type BusinessMetrics = z.infer<typeof BusinessMetricsSchema>;

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

export const BusinessBillingRecordSchema = z.object({
  id: z.string(),
  bookingId: z.string(),
  customerId: z.string(),
  paymentIntentId: z.string().optional(),
  amountCents: z.number(),
  provider: z.string(),
  receiptId: z.string(),
  paidAt: z.string(),
  createdAt: z.string(),
});
export type BusinessBillingRecord = z.infer<typeof BusinessBillingRecordSchema>;

export const BusinessBillingPageSchema = z.object({
  data: z.array(BusinessBillingRecordSchema),
  nextCursor: z.string().optional(),
});
export type BusinessBillingPage = z.infer<typeof BusinessBillingPageSchema>;

// ---------------------------------------------------------------------------
// Cobros (charges + charge_items; docs/CONCEPTS.md — "Cobro" is the POS
// transaction, "Línea" a line inside it; see docs/FLOWS.md §1 for the
// Cobrar → Confirmar → Cobrado flow this powers). A Cobro is immutable once
// created; the only transition after that is Anular (owner-only, not built
// in this pass — see src/api/endpoints/charges.ts).
// ---------------------------------------------------------------------------

export const ChargePaymentMethodSchema = z.enum([
  'efectivo',
  'tarjeta',
  'yappy',
  'transferencia',
  'banco_aliado',
  'otro',
]);
export type ChargePaymentMethod = z.infer<typeof ChargePaymentMethodSchema>;

export const ChargeDiscountTypeSchema = z.enum(['percent', 'fixed']);
export type ChargeDiscountType = z.infer<typeof ChargeDiscountTypeSchema>;

/**
 * `value` is a 1-100 percent for `percent`, integer cents for `fixed`
 * (src/lib/chargeTotals.ts mirrors the server's rounding-and-clamp rule for
 * the on-screen total; the server itself is authoritative).
 */
export const ChargeDiscountSchema = z.object({
  type: ChargeDiscountTypeSchema,
  value: z.number(),
});
export type ChargeDiscount = z.infer<typeof ChargeDiscountSchema>;

export const ChargeItemRecordSchema = z.object({
  id: z.string(),
  /** Null for a free-form "Otro artículo" Línea; never absent (see CONCEPTS.md — Línea). */
  serviceId: z.string().nullable(),
  name: z.string(),
  unitPriceCents: z.number(),
  quantity: z.number(),
  lineTotalCents: z.number(),
});
export type ChargeItemRecord = z.infer<typeof ChargeItemRecordSchema>;

export const ChargeStatusSchema = z.enum(['completed', 'voided']);
export type ChargeStatus = z.infer<typeof ChargeStatusSchema>;

export const ChargeRecordSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  bookingId: z.string().nullable(),
  customerId: z.string().nullable(),
  createdBy: z.string(),
  status: ChargeStatusSchema,
  subtotalCents: z.number(),
  discountCents: z.number(),
  totalCents: z.number(),
  discount: ChargeDiscountSchema.nullable(),
  appliedOfferId: z.string().nullable(),
  paymentMethod: ChargePaymentMethodSchema,
  /** The transfer or receipt number the counter wrote down (migration 0080); older APIs omit it. */
  paymentReference: z.string().nullable().optional(),
  /** 'manual' = the business typed it in (Pagos / Cobrar); 'dale' = processed by Dale. */
  source: z.enum(['manual', 'dale']).optional(),
  /** What was actually received; below `totalCents` it is an abono (partial). */
  amountPaidCents: z.number().optional(),
  note: z.string().nullable(),
  chargedAt: z.string(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
  items: z.array(ChargeItemRecordSchema),
});
export type ChargeRecord = z.infer<typeof ChargeRecordSchema>;

export const ChargeListResponseSchema = z.object({
  data: z.array(ChargeRecordSchema),
  nextCursor: z.string().optional(),
});
export type ChargeListResponse = z.infer<typeof ChargeListResponseSchema>;

// ---------------------------------------------------------------------------
// Gastos (expenses, Phase 6; docs/CONCEPTS.md: "Gasto" is plata que sale.
// Eliminar deactivates one-time or template, Terminar recurrencia sets
// recurringUntil so past months keep the gasto and future months drop it).
// ---------------------------------------------------------------------------

export const ExpenseCategorySchema = z.enum([
  'alquiler',
  'planilla',
  'insumos',
  'servicios',
  'afiliacion_dale',
  'otro',
]);
export type ExpenseCategory = z.infer<typeof ExpenseCategorySchema>;

export const ExpenseRecordSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  concept: z.string(),
  amountCents: z.number(),
  category: ExpenseCategorySchema,
  incurredOn: z.string(),
  isRecurringMonthly: z.boolean(),
  recurringUntil: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ExpenseRecord = z.infer<typeof ExpenseRecordSchema>;

export const ExpensesResponseSchema = z.object({ data: z.array(ExpenseRecordSchema) });
export type ExpensesResponse = z.infer<typeof ExpensesResponseSchema>;

export const ExpenseResponseSchema = z.object({ data: ExpenseRecordSchema });

// ---------------------------------------------------------------------------
// Finanzas (computed P&L view, not a stored object; docs/CONCEPTS.md:
// ingresos = Cobros no anulados en el rango + Reservas completadas SIN Cobro
// vinculado, para no contar dos veces la misma visita).
// ---------------------------------------------------------------------------

export const PnlMonthSchema = z.object({
  month: z.string(),
  revenueCents: z.number(),
  expenseCents: z.number(),
  netCents: z.number(),
});
export type PnlMonth = z.infer<typeof PnlMonthSchema>;

export const PnlResultSchema = z.object({
  from: z.string(),
  to: z.string(),
  revenueCents: z.number(),
  chargeRevenueCents: z.number(),
  bookingRevenueCents: z.number(),
  expenseCents: z.number(),
  netCents: z.number(),
  expensesByCategory: z.partialRecord(ExpenseCategorySchema, z.number()),
  months: z.array(PnlMonthSchema),
});
export type PnlResult = z.infer<typeof PnlResultSchema>;

export const PnlResponseSchema = z.object({ data: PnlResultSchema });

// ---------------------------------------------------------------------------
// Reportes (Finanzas > Reportes). Computed like the P&L: ingresos are what came
// in (amount paid on Cobros no anulados) plus completed Reservas with no Cobro,
// on Panama calendar days, so every figure here agrees with Resumen.
// ---------------------------------------------------------------------------

const ReportPeriodSchema = z.object({ from: z.string(), to: z.string() });

export const ReportSalesTotalsSchema = z.object({
  revenueCents: z.number(),
  grossCents: z.number(),
  discountCents: z.number(),
  netCents: z.number(),
  collectedCents: z.number(),
  outstandingCents: z.number(),
  bookingRevenueCents: z.number(),
  chargeCount: z.number(),
  averageTicketCents: z.number(),
  voidedCount: z.number(),
  voidedCents: z.number(),
  expenseCents: z.number(),
  resultCents: z.number(),
});
export type ReportSalesTotals = z.infer<typeof ReportSalesTotalsSchema>;

export const BusinessReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  previousPeriod: ReportPeriodSchema,
  sales: ReportSalesTotalsSchema,
  previous: ReportSalesTotalsSchema,
  paymentMethods: z.array(
    z.object({
      method: ChargePaymentMethodSchema,
      amountCents: z.number(),
      count: z.number(),
    }),
  ),
  team: z.array(
    z.object({
      staffId: z.string().nullable(),
      name: z.string(),
      revenueCents: z.number(),
      visits: z.number(),
      noShows: z.number(),
    }),
  ),
  services: z.array(
    z.object({
      serviceId: z.string().nullable(),
      name: z.string(),
      quantity: z.number(),
      revenueCents: z.number(),
    }),
  ),
  customers: z.object({
    served: z.number(),
    newCount: z.number(),
    returningCount: z.number(),
    newRevenueCents: z.number(),
    returningRevenueCents: z.number(),
    top: z.array(
      z.object({
        customerId: z.string(),
        name: z.string(),
        revenueCents: z.number(),
        visits: z.number(),
      }),
    ),
  }),
  bookings: z.object({
    completed: z.number(),
    noShow: z.number(),
    cancelled: z.number(),
    noShowValueCents: z.number(),
    cancelledValueCents: z.number(),
    classFillPercent: z.number().nullable(),
  }),
  taxes: z.object({
    invoiceCount: z.number(),
    voidedInvoiceCount: z.number(),
    subtotalCents: z.number(),
    itbmsCents: z.number(),
    totalCents: z.number(),
  }),
  days: z.array(z.object({ date: z.string(), revenueCents: z.number() })),
});
export type BusinessReport = z.infer<typeof BusinessReportSchema>;

export const BusinessReportResponseSchema = z.object({ data: BusinessReportSchema });

export const ReportExportKindSchema = z.enum(['cobros', 'facturas', 'gastos', 'planilla']);
export type ReportExportKind = z.infer<typeof ReportExportKindSchema>;

// Planilla (GET /v1/business/reports/payroll): mirrors services/api/src/business-payroll.ts.
// Every amount is integer cents and every duration whole minutes, never a float.
const wholeCents = z.number().int().nonnegative();

export const PayBasisSchema = z.enum(['class', 'hour']);
export type PayBasis = z.infer<typeof PayBasisSchema>;

export const PayrollSessionSchema = z.object({
  sessionId: z.string(),
  startsAt: z.string(),
  serviceName: z.string(),
  attended: z.number().int().nonnegative(),
  minutes: z.number().int().nonnegative(),
});

export const PayrollRowSchema = z.object({
  staffId: z.string(),
  name: z.string(),
  basis: PayBasisSchema.nullable(),
  rateCents: wholeCents.nullable(),
  classes: z.number().int().nonnegative(),
  minutes: z.number().int().nonnegative(),
  totalCents: wholeCents,
  sessions: z.array(PayrollSessionSchema),
  /** Versiones contra choques (0099): the roster row's version, sent back with a rate edit. */
  version: z.number().int().positive().optional(),
});
export type PayrollRow = z.infer<typeof PayrollRowSchema>;

export const PayrollReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  cutoff: z.string(),
  rows: z.array(PayrollRowSchema),
  totalCents: wholeCents,
});
export type PayrollReport = z.infer<typeof PayrollReportSchema>;

export const PayrollReportResponseSchema = z.object({ data: PayrollReportSchema });

/** PUT /v1/business/staff/:staffId/pay-rate: a rate and its basis, or both null. */
export const StaffPayRateSchema = z.object({
  staffId: z.string(),
  rateCents: wholeCents.nullable(),
  basis: PayBasisSchema.nullable(),
  /** The roster row's version after the save (or as it stands, in a 409's `current`). */
  version: z.number().int().positive().optional(),
});
export type StaffPayRate = z.infer<typeof StaffPayRateSchema>;

export const StaffPayRateResponseSchema = z.object({ data: StaffPayRateSchema });

// ---------------------------------------------------------------------------
// Facturas (docs/CONCEPTS.md: Factura, emitida -> anulada, FA-series)
// ---------------------------------------------------------------------------

export const FiscalProfileSchema = z.object({
  businessId: z.string(),
  ruc: z.string(),
  dv: z.string(),
  razonSocial: z.string(),
  direccion: z.string(),
  itbmsDefaultRateBp: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Versiones contra choques (0099): the document's version, sent back as expectedVersion (0 before the first save). */
  version: z.number().int().nonnegative().optional(),
});
export type FiscalProfile = z.infer<typeof FiscalProfileSchema>;

export const FiscalProfileResponseSchema = z.object({ data: FiscalProfileSchema });

export const InvoiceStatusSchema = z.enum(['emitida', 'anulada']);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const InvoiceRecordSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  chargeId: z.string(),
  displayNumber: z.string(),
  seriesNumber: z.number(),
  status: InvoiceStatusSchema,
  clientName: z.string().nullable(),
  clientRuc: z.string().nullable(),
  clientDv: z.string().nullable(),
  subtotalCents: z.number(),
  discountCents: z.number(),
  itbmsRateBp: z.number(),
  itbmsCents: z.number(),
  totalCents: z.number(),
  cufe: z.string().nullable(),
  /** The handle the public share page is opened with (migration 0041). */
  shareToken: z.string(),
  issuedAt: z.string(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
});
export type InvoiceRecord = z.infer<typeof InvoiceRecordSchema>;

export const InvoiceResponseSchema = z.object({ data: InvoiceRecordSchema });
export const InvoicesResponseSchema = z.object({ data: z.array(InvoiceRecordSchema) });
export type InvoicesResponse = z.infer<typeof InvoicesResponseSchema>;

/**
 * "Emitir factura al cobrar": with the switch on, the Cobro comes back with its
 * Factura, or with why there is none. With it off, both fields are absent.
 */
export const AutoInvoiceSkipSchema = z.enum(['no_fiscal_profile', 'plan', 'failed']);
export type AutoInvoiceSkip = z.infer<typeof AutoInvoiceSkipSchema>;

export const ChargeCreatedSchema = ChargeRecordSchema.extend({
  invoice: InvoiceRecordSchema.optional(),
  invoiceSkipped: AutoInvoiceSkipSchema.optional(),
});
export type ChargeCreated = z.infer<typeof ChargeCreatedSchema>;

export const InvoiceSettingsSchema = z.object({ autoInvoice: z.boolean() });
export type InvoiceSettings = z.infer<typeof InvoiceSettingsSchema>;
export const InvoiceSettingsResponseSchema = z.object({ data: InvoiceSettingsSchema });

export const InvoiceSendStatusSchema = z.enum(['sent', 'failed']);

/** One recorded attempt at mailing a factura. */
export const InvoiceSendSchema = z.object({
  id: z.string(),
  invoiceId: z.string(),
  businessId: z.string(),
  email: z.string(),
  status: InvoiceSendStatusSchema,
  providerId: z.string().nullable(),
  createdAt: z.string(),
});
export type InvoiceSend = z.infer<typeof InvoiceSendSchema>;

export const InvoiceSendResponseSchema = z.object({ data: InvoiceSendSchema });

/**
 * La factura como la abre el cliente: the public projection, which carries only
 * what the printed sheet shows. No ids, no business id, no charge id.
 */
export const PublicInvoiceSchema = z.object({
  emisor: z.object({
    razonSocial: z.string(),
    ruc: z.string(),
    dv: z.string(),
    direccion: z.string(),
  }),
  displayNumber: z.string(),
  status: InvoiceStatusSchema,
  issuedAt: z.string(),
  clientName: z.string().nullable(),
  clientRuc: z.string().nullable(),
  clientDv: z.string().nullable(),
  subtotalCents: z.number(),
  discountCents: z.number(),
  itbmsRateBp: z.number(),
  itbmsCents: z.number(),
  totalCents: z.number(),
  cufe: z.string().nullable(),
  voidReason: z.string().nullable(),
  items: z.array(
    z.object({
      name: z.string(),
      quantity: z.number(),
      lineTotalCents: z.number(),
    }),
  ),
});
export type PublicInvoice = z.infer<typeof PublicInvoiceSchema>;

export const PublicInvoiceResponseSchema = z.object({ data: PublicInvoiceSchema });

// ---------------------------------------------------------------------------
// Mesas (el plano físico de un restaurante; docs/CONCEPTS.md, migration
// 0019). Reassigning a Reserva's mesa reuses the allocator's pickTable rule,
// which is why a conflict comes back as the same 'ocupada' message whether
// the mesa is inactive, too small, or already held for an overlapping window.
// ---------------------------------------------------------------------------

export const BusinessTableSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  name: z.string(),
  seats: z.number(),
  zone: z.string(),
  gridX: z.number(),
  gridY: z.number(),
  gridW: z.number(),
  gridH: z.number(),
  isActive: z.boolean(),
  kind: z.string(),
  isPrivate: z.boolean(),
  shape: z.enum(['circle', 'square', 'rectangle']),
  rotation: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Versiones contra choques (0099): sent back as expectedVersion on an edit or a move. */
  version: z.number().int().positive().optional(),
});
export type BusinessTable = z.infer<typeof BusinessTableSchema>;

export const BusinessTableListResponseSchema = z.object({ data: z.array(BusinessTableSchema) });
export const BusinessTableResponseSchema = z.object({ data: BusinessTableSchema });

// ---------------------------------------------------------------------------
// Suscripciones (`subscription_plans` / `customer_subscriptions`, migration
// 0028). A Plan is what the studio sells; a Suscripción is one customer's copy
// of it, with the plan's name, price and pack size frozen at the sale so a
// later edit to the Plan never rewrites what somebody already bought.
// ---------------------------------------------------------------------------

export const SubscriptionPlanKindSchema = z.enum(['pack', 'unlimited']);
export type SubscriptionPlanKind = z.infer<typeof SubscriptionPlanKindSchema>;

export const SubscriptionPlanSchema = z.object({
  ...PackTermsShape,
  id: z.string(),
  businessId: z.string(),
  name: z.string(),
  kind: SubscriptionPlanKindSchema,
  creditCount: z.number().nullable(),
  priceCents: z.number(),
  validityDays: z.number(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Versiones contra choques (0099): sent back as expectedVersion on an edit. */
  version: z.number().int().positive().optional(),
});
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

export const CustomerSubscriptionSchema = z.object({
    ...PackTermsShape,
    balances: PackBalancesSchema.nullable().optional(),
    history: PackHistorySchema.optional(),
  id: z.string(),
  businessId: z.string(),
  planId: z.string(),
  customerId: z.string(),
  planName: z.string(),
  priceCents: z.number(),
  kind: SubscriptionPlanKindSchema,
  creditsRemaining: z.number().nullable(),
  creditsTotal: z.number().nullable(),
  startsAt: z.string(),
  expiresAt: z.string(),
  status: z.enum(['active', 'cancelled']),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomerSubscription = z.infer<typeof CustomerSubscriptionSchema>;

export const SubscriptionPlanListResponseSchema = z.object({ data: z.array(SubscriptionPlanSchema) });
export const SubscriptionPlanResponseSchema = z.object({ data: SubscriptionPlanSchema });
export const CustomerSubscriptionListResponseSchema = z.object({
  data: z.array(CustomerSubscriptionSchema),
});
/** A sale answers with the Suscripción and the Cobro it produced. */
export const SellSubscriptionResponseSchema = z.object({
  data: z.object({
    subscription: CustomerSubscriptionSchema,
    // The Cobro as POST /charges answers it, so an automatic Factura (or why none) survives the parse.
    charge: ChargeCreatedSchema.nullable(),
  }),
  chargeError: z.object({ code: z.string(), message: z.string() }).optional(),
});

// ---------------------------------------------------------------------------
// Floor plan image (`business_floor_plans`, migration 0026). One per
// business; `GET` answers `null` before an owner has drawn one.
// ---------------------------------------------------------------------------

export const BusinessFloorPlanSchema = z
  .object({
    url: z.string(),
    width: z.number(),
    height: z.number(),
  })
  .nullable();
export type BusinessFloorPlan = z.infer<typeof BusinessFloorPlanSchema>;

// ---------------------------------------------------------------------------
// Public floor read (`GET /v1/businesses/:id/floor`). Unauthenticated, so the
// team can render the roster without owner-only table access. Coordinates are
// permille of the plan canvas, pre-renamed server-side (x/y/w/h).
// ---------------------------------------------------------------------------

export const PublicFloorResourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.string(),
  seats: z.number(),
  isPrivate: z.boolean(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  shape: z.enum(['circle', 'square', 'rectangle']),
  rotation: z.number(),
  available: z.boolean(),
});
export type PublicFloorResource = z.infer<typeof PublicFloorResourceSchema>;

export const PublicFloorReadSchema = z.object({
  floorPlan: BusinessFloorPlanSchema,
  resources: z.array(PublicFloorResourceSchema),
});
export type PublicFloorRead = z.infer<typeof PublicFloorReadSchema>;

// ---------------------------------------------------------------------------
// Floor-plan vision proposals (`POST /v1/business/floor-plan/analyze`). The
// model proposes; the owner commits any of these through the normal mesas
// CRUD above (`BusinessTableSchema`). Nothing here is ever written server-side.
// ---------------------------------------------------------------------------

export const FloorPlanResourceProposalSchema = z.object({
  name: z.string(),
  kind: z.enum(['table', 'room', 'bar', 'bike', 'mat']),
  isPrivate: z.boolean(),
  seats: z.number(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  shape: z.enum(['circle', 'square', 'rectangle']),
  rotation: z.number(),
  zone: z.string().optional(),
});
export type FloorPlanResourceProposal = z.infer<typeof FloorPlanResourceProposalSchema>;

export const FloorPlanZoneProposalSchema = z.object({
  name: z.string(),
  isPrivate: z.boolean().default(false),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
});
export type FloorPlanZoneProposal = z.infer<typeof FloorPlanZoneProposalSchema>;

export const FloorPlanAnalysisResponseSchema = z.object({
  resources: z.array(FloorPlanResourceProposalSchema),
  zones: z.array(FloorPlanZoneProposalSchema).default([]),
});
export type FloorPlanAnalysisResponse = z.infer<typeof FloorPlanAnalysisResponseSchema>;

// ---------------------------------------------------------------------------
// Dashboard (one call, no client waterfall)
// ---------------------------------------------------------------------------

export const PAYMENT_METHOD_VALUES = ['efectivo', 'tarjeta', 'yappy', 'otro'] as const;
export const PaymentMethodSchema = z.enum(PAYMENT_METHOD_VALUES);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const DashboardSchema = z.object({
  today: z.object({
    bookingsTotal: z.number(),
    porCerrar: z.number(),
    revenueCents: z.number(),
    next: z.array(BookingSchema),
  }),
  month: z.object({
    ingresosCents: z.number(),
    gastosCents: z.number(),
    netoCents: z.number(),
    // Every method a charge can carry: Pagos records transferencias, so the
    // summary must read them or the whole Inicio fails to load.
    byMethod: z.array(
      z.object({ method: ChargePaymentMethodSchema, totalCents: z.number() }),
    ),
  }),
});
export type Dashboard = z.infer<typeof DashboardSchema>;
export const DashboardResponseSchema = z.object({ data: DashboardSchema });

// ---------------------------------------------------------------------------
// Locations (multi-local organizations; GET /v1/business/locations, POST
// /v1/business/session/location — owner-only, see business-session-routes.ts)
// ---------------------------------------------------------------------------

export const BusinessLocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: CategorySchema,
  neighborhood: BusinessPlaceSchema,
  active: z.boolean(),
});
export type BusinessLocation = z.infer<typeof BusinessLocationSchema>;

export const BusinessLocationListResponseSchema = z.object({ data: z.array(BusinessLocationSchema) });

export const BusinessLocationSwitchResponseSchema = z.object({ businessId: z.string() });

// ---------------------------------------------------------------------------
// Empty / 204 responses
// ---------------------------------------------------------------------------

export const EmptyResponseSchema = z.undefined();

// ---------------------------------------------------------------------------
// Inteligencia (Max tier; computed server-side, model writes only the
// briefing prose)
// ---------------------------------------------------------------------------

/** "Esto te está faltando": what the business's own profile is missing. */
export const IntelligenceSetupGapSchema = z.enum(['services', 'hours', 'staff', 'photos', 'prices', 'tagline', 'expenses']);
export type IntelligenceSetupGap = z.infer<typeof IntelligenceSetupGapSchema>;

export const IntelligenceActionSchema = z.union([
  z.object({
    screen: z.literal('ofertas'),
    params: z.object({ weekday: z.number(), startHour: z.number(), endHour: z.number() }),
  }),
  z.object({
    screen: z.literal('clientes'),
    params: z.object({ segment: z.literal('win_back'), customerIds: z.array(z.string()) }),
  }),
  z.object({
    screen: z.literal('servicios'),
    params: z.object({ focus: z.literal('margin') }),
  }),
  z.object({
    screen: z.literal('agenda'),
    params: z.object({ status: z.literal('no_show'), staffId: z.string(), hour: z.number() }),
  }),
  // A setup gap points at the screen where it is fixed (missing expenses: Finanzas).
  z.object({
    screen: z.enum(['servicios', 'agenda', 'equipo', 'negocio', 'finanzas']),
    params: z.object({ gap: IntelligenceSetupGapSchema }),
  }),
]);
export type IntelligenceAction = z.infer<typeof IntelligenceActionSchema>;

export const IntelligenceInsightSchema = z.object({
  type: z.enum(['dead_hours', 'churned_customers', 'margin', 'staff_no_show', 'setup_gap']),
  title: z.string(),
  detail: z.string(),
  metrics: z.record(z.string(), z.unknown()),
  // "Cómo arreglarlo". Optional so a client can outlive a worker that predates it.
  steps: z.array(z.string()).optional(),
  action: IntelligenceActionSchema,
});
export type IntelligenceInsight = z.infer<typeof IntelligenceInsightSchema>;

/** One asesor of the Max-tier agency: a briefing over the insights it owns. */
export const IntelligencePersonaSchema = z.object({
  personaId: z.enum(['direccion', 'marketing', 'finanzas', 'operaciones']),
  name: z.string(),
  title: z.string(),
  insights: z.array(IntelligenceInsightSchema.shape.type),
  // Which setup gaps this asesor draws on; absent when it has none.
  gaps: z.array(IntelligenceSetupGapSchema).optional(),
  narration: z.string().optional(),
  quietNote: z.string().optional(),
});
export type IntelligencePersona = z.infer<typeof IntelligencePersonaSchema>;

export const IntelligenceResponseSchema = z.object({
  data: z.object({
    generatedAt: z.string(),
    insights: z.array(IntelligenceInsightSchema),
    // Optional so a client can outlive a worker that predates the asesores.
    personas: z.array(IntelligencePersonaSchema).optional(),
    briefing: z.string().optional(),
  }),
});
export type IntelligenceResponse = z.infer<typeof IntelligenceResponseSchema>;


export const ClassSessionSchema = z.object({
  policy: ClassPolicySchema.optional(),
  id: z.string().min(1), businessId: z.string().min(1), serviceId: z.string().min(1), instructorId: z.string().min(1),
  startsAt: z.string().datetime({ offset: true }), endsAt: z.string().datetime({ offset: true }),
  capacity: z.number().int().positive(), resourceIds: z.array(z.string().min(1)), priceCents: z.number().int().nonnegative(),
  status: z.enum(['published', 'cancelled']), version: z.number().int().positive(),
  availableCapacity: z.number().int().nonnegative(), availableResourceIds: z.array(z.string().min(1)),
});
export const ClassSessionListSchema = z.object({ data: z.array(ClassSessionSchema) });

// Step08 merchant payments (mirrors public CheckoutSchema in services/api/src/merchant-payment-contract.ts).
const PaymentIdSchema = z.string().trim().min(1).max(200);
export const PAYMENT_MAX_CENTS = 100_000_000;
export const PaymentAmountSchema = z.number().int().positive().max(PAYMENT_MAX_CENTS);
export const MerchantProviderSchema = z.enum(['yappy', 'paguelofacil']);
export const CheckoutTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('booking'), bookingId: PaymentIdSchema }).strict(),
  z.object({ kind: z.literal('pack'), businessId: PaymentIdSchema, planId: PaymentIdSchema }).strict(),
]);
export const CheckoutRequestSchema = z.object({
  target: CheckoutTargetSchema, provider: MerchantProviderSchema, expectedAmountCents: PaymentAmountSchema,
  reviewId: z.string().uuid().optional(), confirmed: z.literal(true).optional(),
}).strict();
export const MerchantRefundSchema = z.object({
  id: PaymentIdSchema,
  status: z.enum(['pending', 'business_review', 'approved', 'declined', 'submitted', 'refunded', 'failed']),
  requestedAmountCents: PaymentAmountSchema, approvedAmountCents: PaymentAmountSchema.nullable(),
  reason: z.enum(['customer_request', 'fulfillment_failed', 'booking_cancelled']), ownerTask: z.boolean(),
  providerRefundId: PaymentIdSchema.nullable(), reviewedBy: PaymentIdSchema.nullable(), updatedAt: z.string().datetime(),
});
export const MerchantCheckoutReceiptSchema = z.object({
  id: PaymentIdSchema, chargeId: PaymentIdSchema, amountCents: PaymentAmountSchema,
  currency: z.literal('USD'), paidAt: z.string().datetime(),
});
export const MerchantPaymentActionSchema = z.object({ type: z.literal('yappy_button'), environment: z.enum(['test', 'live']),
  transactionId: z.string().trim().min(1).max(200), documentName: z.string().trim().min(1).max(200),
  token: z.string().min(1).max(8192), expiresAt: z.string().datetime() }).strict();
const YappyLaunchUrlSchema = z.string().url().max(2048).refine(value => {
  const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password;
});
export const YappyLaunchSchema = z.object({ checkoutId: z.string().trim().min(1).max(200),
  businessId: z.string().trim().min(1).max(200), customerId: z.string().trim().min(1).max(200),
  amountCents: PaymentAmountSchema, currency: z.literal('USD'), expiresAt: z.string().datetime(),
  launchUrl: YappyLaunchUrlSchema, returnUrl: YappyLaunchUrlSchema }).strict();
export const YappyLaunchActionSchema = YappyLaunchSchema.extend({ paymentAction: MerchantPaymentActionSchema }).strict();
export type YappyLaunch = z.infer<typeof YappyLaunchSchema>;
export type YappyLaunchAction = z.infer<typeof YappyLaunchActionSchema>;
export const MerchantCheckoutSchema = z.object({
  id: PaymentIdSchema, businessId: PaymentIdSchema, customerId: PaymentIdSchema, target: CheckoutTargetSchema,
  provider: MerchantProviderSchema, status: z.enum(['pending', 'paid', 'failed', 'cancelled', 'expired']),
  /* Customer-only while pending and unexpired; never payment proof. */ hostedUrl: z.string().url().nullable(), amountCents: PaymentAmountSchema, currency: z.literal('USD'),
  paymentAction: MerchantPaymentActionSchema.nullable().default(null), paymentReviewRequired: z.boolean().default(false),
  fulfillmentStatus: z.enum(['pending', 'fulfilled', 'business_review']), fulfillmentId: PaymentIdSchema.nullable(),
  receipt: MerchantCheckoutReceiptSchema.nullable(), refund: MerchantRefundSchema.nullable(),
  createdAt: z.string().datetime(), expiresAt: z.string().datetime(), updatedAt: z.string().datetime(),
});
export const MerchantReadinessSchema = z.object({
  provider: MerchantProviderSchema, checkout: z.boolean(), prepay: z.boolean(), refunds: z.boolean(),
  environment: z.enum(['test', 'live']).nullable(),
});
export type MerchantCheckout = z.infer<typeof MerchantCheckoutSchema>;
export type MerchantRefund = z.infer<typeof MerchantRefundSchema>;

// Direct booking reviews are server-issued and required for held class mutations.
export const BookingReviewSchema = z.object({ id: z.string().uuid(), bookingId: z.string().min(1),
  paymentChoice: BookingPaymentChoiceSchema, expiresAt: z.string().datetime(), booking: BookingSchema }).strict();
export const BookingReviewResponseSchema = z.object({ data: BookingReviewSchema });
export const BookingPaymentReadinessSchema = z.object({ data: z.object({ methods: z.array(BookingPaymentChoiceSchema) }).strict() });

// Consumer current-plan review and owned checkout history, Step17.
export const ConsumerPurchasePlanSchema = z.object({
  currency: z.literal('USD'),
  id: PaymentIdSchema, name: z.string().min(1).max(200), kind: z.enum(['pack', 'unlimited']),
  creditCount: z.number().int().positive().nullable(), priceCents: PaymentAmountSchema,
  validityDays: z.number().int().positive().max(3650), eligibleServiceIds: z.array(PaymentIdSchema).nullable(),
  sessionOnly: z.boolean(), policySnapshot: z.object({ cutoffMinutes: z.number().int().min(0).max(43200),
    lateCancellationConsumesCredit: z.boolean(), noShowConsumesCredit: z.boolean() }).strict().nullable(),
});
export const ConsumerPurchaseReviewSchema = z.object({ id: z.string().uuid(), businessId: PaymentIdSchema,
  plan: ConsumerPurchasePlanSchema, provider: MerchantProviderSchema, currency: z.literal('USD'), expiresAt: z.string().datetime() });
export const ConsumerPurchaseCatalogSchema = z.object({ data: z.object({ plans: z.array(ConsumerPurchasePlanSchema).max(100),
  providers: z.array(z.object({ provider: MerchantProviderSchema, checkout: z.boolean(), prepay: z.boolean(), refunds: z.boolean(),
    environment: z.enum(['test', 'live']).nullable() })).max(2) }) });
export const ConsumerPurchaseReviewResponseSchema = z.object({ data: ConsumerPurchaseReviewSchema });
export const ConsumerCheckoutHistorySchema = z.object({ data: z.array(MerchantCheckoutSchema.extend({ hostedUrl: z.null(),
  paymentAction: z.null(), operation: z.object({ idempotencyKey: PaymentIdSchema, request: CheckoutRequestSchema }).strict() })).max(50),
  nextCursor: PaymentIdSchema.nullable() });
export type ConsumerPurchasePlan = z.infer<typeof ConsumerPurchasePlanSchema>;
export type ConsumerPurchaseReview = z.infer<typeof ConsumerPurchaseReviewSchema>;
export type ConsumerCheckoutHistory = z.infer<typeof ConsumerCheckoutHistorySchema>;
